import { createFileRoute } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, H3 } from '../components/docs/prose'

export const Route = createFileRoute('/docs/importing')({ component: Importing })

const SECTIONS = [
  { id: 'why', title: 'Why import at all' },
  { id: 'getting', title: 'Getting the file' },
  { id: 'wizard', title: 'The import wizard' },
  { id: 'templates', title: 'Saved templates' },
  { id: 'unknown', title: 'Rows it cannot classify' },
  { id: 'idempotent', title: 'Re-importing is safe' },
  { id: 'known-formats', title: 'Wealthsimple, TD and Qtrade' },
  { id: 'currency', title: 'Currency' },
  { id: 'limits', title: 'What it will not do' },
]

function Importing() {
  return (
    <DocPage
      path="/docs/importing"
      title="Importing brokerage data"
      lede={
        <>
          Bank connections give you balances. Adjusted cost base needs
          transactions &mdash; what you bought, when, and at what price. For most
          Canadian brokers an activity export is the only way to get them, so
          the importer accepts any broker&rsquo;s CSV: you tell it once which
          column is which, and it remembers.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="why" title="Why import at all">
        <P>
          A linked account reports what it holds today. That is enough for net
          worth and nowhere near enough for ACB, which is a running number built
          from every purchase you have ever made.
        </P>
        <P>
          Some institutions serve transaction history through Plaid. Several
          large Canadian ones do not &mdash; Wealthsimple, for instance, returns
          an empty result for investment transactions even when the connection
          is authorised for them. That is not a bug you can configure around, so
          the CSV path exists.
        </P>
      </Section>

      <Section id="getting" title="Getting the file">
        <DefList
          items={[
            [
              'Wealthsimple',
              'Activity → Export → CSV. Pick the widest date range offered; the importer only ever adds what it does not already have, so more history costs nothing.',
            ],
            [
              'TD Direct Investing (WebBroker)',
              'Accounts → Activity → Download, and separately Holdings → Download. Take both: the activity file carries trades and distributions, and the holdings file is the only source of book cost for that account.',
            ],
            [
              'Any other broker',
              'Whatever activity or transaction export it offers, as CSV, unchanged. The wizard does not need to recognise the format — that is the point of it.',
            ],
          ]}
        />
      </Section>

      <Section id="wizard" title="The import wizard">
        <P>
          The wizard walks five steps: <strong>upload</strong>, <strong>columns</strong>,{' '}
          <strong>activity types</strong>, <strong>review</strong>, <strong>done</strong>.
          Nothing is written until the final step; everything before it is a
          preview you can back out of.
        </P>
        <H3>Upload</H3>
        <P>
          You pick the file and the target account up front. The fixed-format
          importers could read the account out of the file; a generic export
          names no account FolioWorth could recognise, and guessing would put
          one account&rsquo;s trades in another&rsquo;s ACB pool. If you have a
          saved template for this broker, you pick it here too.
        </P>
        <H3>Columns</H3>
        <P>
          Each field &mdash; date, activity type, symbol, quantity, price, fees,
          amount, currency, description &mdash; is mapped to one of the
          file&rsquo;s column headers. The wizard pre-fills this mapping from
          the headers themselves (<C>Trade Date</C> lands on date,{' '}
          <C>Commission</C> on fees, and so on), but conservatively: a field it
          is not sure about is left blank for you to fill rather than guessed,
          because a wrong pre-fill that looks confident is worse than a visible
          gap. Only the date is mandatory &mdash; a cash-only statement has no
          symbols or quantities, and that is fine. The date format is detected
          from the rows when possible, and shown so you can correct it; the one
          case detection cannot settle is a file where day and month are both
          always ≤ 12, which only you can answer.
        </P>
        <H3>Activity types</H3>
        <P>
          Brokers invent their own vocabulary: <C>BOT</C>, <C>SLD</C>,{' '}
          <C>DIST</C>. This step lists every distinct code found in the file and
          what it currently maps to &mdash; buy, sell, dividend, interest, fee,
          deposit, withdrawal, transfer, split, return of capital. A built-in
          vocabulary already covers the common spellings, so most files arrive
          largely classified; you only teach it the codes it does not know.
        </P>
        <H3>Review and commit</H3>
        <P>
          The review table shows every row with its status: valid, warning
          (imported, with a note &mdash; a price derived from amount and
          quantity, say), needs review, or error. Committing converts exactly
          the rows you previewed &mdash; the parsed batch is stored on upload
          and the commit reads it back, rather than re-parsing the file, so
          what you approved is what gets written. Error rows and unanswered
          needs-review rows are skipped and counted, never silently dropped.
        </P>
      </Section>

      <Section id="templates" title="Saved templates">
        <P>
          At the review step you can save the mapping &mdash; columns, activity
          codes, date format, decimal style &mdash; under a name. Next
          month&rsquo;s statement from the same broker is then a two-click
          import: pick the template at upload, confirm the preview, commit.
        </P>
        <P>
          A template is a starting point, not a cage: applying one and then
          adjusting a column for this file does not edit the saved copy.
          Deleting a template does not touch imports already made with it.
        </P>
      </Section>

      <Section id="unknown" title="Rows it cannot classify">
        <P>
          An activity code that neither the built-in vocabulary nor your
          template recognises maps to <C>UNKNOWN</C> and the row is flagged{' '}
          <strong>needs review</strong>. The broker&rsquo;s own code is kept
          verbatim on the row &mdash; an unhandled type once silently cost an
          import 24 trades, so the code must survive for you to classify it.
        </P>
        <P>
          A needs-review row imports as nothing until you answer it, either by
          mapping the code at the types step or by setting the row&rsquo;s type
          in the review table. Ignoring it is allowed; the row is counted as
          skipped so the gap is visible.
        </P>
      </Section>

      <Section id="idempotent" title="Re-importing is safe">
        <P>
          Import the same file twice, or overlapping exports from different
          months, and nothing is duplicated. Every row is matched two ways
          &mdash; the same convention the fixed-format importers use, because
          both commit through the same path:
        </P>
        <DefList
          items={[
            [
              'A stable identifier',
              'Built from what the broker literally wrote — date, action, symbol, quantity, price, amount, description, exactly as the cells carried them. Deliberately never from anything FolioWorth derives, because improving the parser would then change the key and turn every previously imported row into a new one.',
            ],
            [
              'The natural key',
              'Same account, same day, same action, same quantity, same price. This holds even if the identifier scheme changes, so dedupe never depends on a constructed string alone.',
            ],
          ]}
        />
        <P>
          A committed batch also refuses to commit twice, and the result screen
          reports what was created against what was skipped as already present.
          The upshot is a monthly routine you can run without thinking: download
          the latest export, drop it in, and only genuinely new activity lands.
        </P>
      </Section>

      <Section id="known-formats" title="Wealthsimple, TD and Qtrade">
        <P>
          The brokers FolioWorth used to recognise by content still import.
          Wealthsimple and TD activity CSVs go through the same wizard as
          everything else &mdash; their headers are exactly what the
          auto-suggestion was built on and their activity codes are in the
          built-in vocabulary, so they typically sail through with nothing to
          fix. Because old and new paths share one commit convention, history
          you imported under the old fixed-format flow is not duplicated when
          the same export passes through the wizard.
        </P>
        <P>
          Qtrade is the exception that proves why content detection existed:
          its &ldquo;export&rdquo; is an HTML table wearing a <C>.xls</C>{' '}
          filename, not a CSV, so the wizard cannot read it. The
          content-detecting importer that handles it (and the TD holdings file,
          which carries book cost rather than activity) remains in place behind
          the same API.
        </P>
        <H3>Where symbols come from</H3>
        <P>
          TD activity exports name the security rather than its ticker
          (&ldquo;RESTAURANT BRANDS INTL&rdquo;), so tickers are resolved against
          the holdings file for the same account. Import the holdings export
          alongside the activity one and the symbols resolve; import it alone and
          you will see descriptions where you expected tickers.
        </P>
      </Section>

      <Section id="currency" title="Currency">
        <P>
          If the file has a currency column, map it and every row carries its
          own. Many exports state no currency anywhere &mdash; a TD activity
          file is simply denominated in whatever the account is &mdash; and
          rows without one are treated as Canadian dollars, so check the
          mapping on a US-dollar account&rsquo;s export: an unmapped currency
          column would understate it by roughly the exchange rate while every
          individual figure still looked plausible.
        </P>
        <P>
          Amounts are shown in the currency they were earned in, and converted
          before entering any total.
        </P>
      </Section>

      <Section id="limits" title="What it will not do">
        <DefList
          items={[
            [
              'Guess a missing cost basis',
              'A transfer in from another institution with no book cost recorded stays unknown rather than being filled with market value on the transfer date, which would be wrong by the entire gain to that point.',
            ],
            [
              'Fabricate purchase dates',
              'Positions reported by an institution with no trade history behind them are labelled as such, and show the brokerage’s own cost figure rather than a calculated ACB.',
            ],
            [
              'Import deposits and splits as holdings',
              'Deposits and withdrawals are your own money moving, not positions; they are counted and skipped. Splits are previewed honestly as recorded-but-not-imported, because applying one correctly needs corporate-action handling the ACB engine does not yet have.',
            ],
            [
              'Adjust for superficial losses',
              'The ±30 day rule is not applied automatically. Where it might bite, the disposition is flagged so you can check it — not silently adjusted.',
            ],
          ]}
        />
      </Section>
    </DocPage>
  )
}
